import classNames from "classnames";
import { LocalDate } from "@js-joda/core";
import React from "react";
import { CalendarType, default as ReactCalendar, Detail } from "react-calendar";
import {
  MfCalendarTileProperties,
  MfClickWeekNumberCallback,
  MfDateCallback,
  MfFormatterCallback,
  MfOnChangeDateCallback,
  MfViewCallback,
} from "../CalendarPickerTypes";
import {
  convertClickWeekNumberCallback,
  convertDateCallback,
  convertFormatterCallback,
  convertLocalDateToDate,
  convertLocalDateToDate2,
  convertNavigationLabel,
  convertOnChangeDateCallback,
  convertTileClassName,
  convertTileContent,
  convertTileDisabled,
  convertViewCallback,
} from "../utils/calendarConversionUtils";

/**
 * PLEASE NOTE!
 *
 * This component is a simple bridge between our app
 * and `react-calendar` by swapping Date with LocalDate
 */
interface Props {
  activeStartDate?: LocalDate;
  allowPartialRange?: boolean;
  calendarType?: CalendarType;
  className?: string | string[];
  defaultActiveStartDate?: LocalDate;
  defaultValue?: LocalDate | LocalDate[];
  defaultView?: Detail;
  formatLongDate?: MfFormatterCallback;
  formatMonth?: MfFormatterCallback;
  formatMonthYear?: MfFormatterCallback;
  formatShortWeekday?: MfFormatterCallback;
  formatYear?: MfFormatterCallback;
  locale?: string;
  maxDate?: LocalDate;
  maxDetail?: Detail;
  minDate?: LocalDate;
  minDetail?: Detail;
  navigationAriaLabel?: string;
  navigationLabel?: (props: {
    date: LocalDate;
    view: Detail;
    label: string;
  }) => string | JSX.Element | null;
  next2AriaLabel?: string;
  next2Label?: string | JSX.Element | null;
  nextAriaLabel?: string;
  nextLabel?: string | JSX.Element;
  onActiveStartDateChange?: MfViewCallback;
  onChange?: MfOnChangeDateCallback;
  onViewChange?: MfViewCallback;
  onClickDay?: MfDateCallback;
  onClickDecade?: MfDateCallback;
  onClickMonth?: MfDateCallback;
  onClickWeekNumber?: MfClickWeekNumberCallback;
  onClickYear?: MfDateCallback;
  onDrillDown?: MfViewCallback;
  onDrillUp?: MfViewCallback;
  prev2AriaLabel?: string;
  prev2Label?: string | JSX.Element | null;
  prevAriaLabel?: string;
  prevLabel?: string | JSX.Element;
  returnValue?: "start" | "end" | "range";
  selectRange?: boolean;
  showDoubleView?: boolean;
  showFixedNumberOfWeeks?: boolean;
  showNavigation?: boolean;
  showNeighboringMonth?: boolean;
  showWeekNumbers?: boolean;
  tileClassName?:
    | string
    | string[]
    | ((props: MfCalendarTileProperties) => string | string[] | null);
  tileContent?: JSX.Element | ((props: MfCalendarTileProperties) => JSX.Element | null);
  tileDisabled?: (props: MfCalendarTileProperties & { activeStartDate: LocalDate }) => boolean;
  value?: LocalDate | LocalDate[] | null;
  view?: Detail;
}

const Calendar = (props: Props) => {
  const activeStartDate = React.useMemo(() => {
    return convertLocalDateToDate(props.activeStartDate);
  }, [props.activeStartDate]);

  const defaultActiveStartDate = React.useMemo(() => {
    return convertLocalDateToDate(props.defaultActiveStartDate);
  }, [props.defaultActiveStartDate]);

  const defaultValue = React.useMemo(() => {
    return convertLocalDateToDate2(props.defaultValue);
  }, [props.defaultValue]);

  const formatLongDate = React.useMemo(() => {
    return convertFormatterCallback(props.formatLongDate);
  }, [props.formatLongDate]);

  const formatMonth = React.useMemo(() => {
    return convertFormatterCallback(props.formatMonth);
  }, [props.formatMonth]);

  const formatMonthYear = React.useMemo(() => {
    return convertFormatterCallback(props.formatMonthYear);
  }, [props.formatMonthYear]);

  const formatShortWeekday = React.useMemo(() => {
    return convertFormatterCallback(props.formatShortWeekday);
  }, [props.formatShortWeekday]);

  const formatYear = React.useMemo(() => {
    return convertFormatterCallback(props.formatYear);
  }, [props.formatYear]);

  const maxDate = React.useMemo(() => {
    return convertLocalDateToDate(props.maxDate);
  }, [props.maxDate]);

  const minDate = React.useMemo(() => {
    return convertLocalDateToDate(props.minDate);
  }, [props.minDate]);

  const navigationLabel = React.useMemo(() => {
    return convertNavigationLabel(props.navigationLabel);
  }, [props.navigationLabel]);

  const onActiveStartDateChange = React.useMemo(() => {
    return convertViewCallback(props.onActiveStartDateChange);
  }, [props.onActiveStartDateChange]);

  const onChange = React.useMemo(() => {
    return convertOnChangeDateCallback(props.onChange);
  }, [props.onChange]);

  const onViewChange = React.useMemo(() => {
    return convertViewCallback(props.onViewChange);
  }, [props.onViewChange]);

  const onClickDay = React.useMemo(() => {
    return convertDateCallback(props.onClickDay);
  }, [props.onClickDay]);

  const onClickDecade = React.useMemo(() => {
    return convertDateCallback(props.onClickDecade);
  }, [props.onClickDecade]);

  const onClickMonth = React.useMemo(() => {
    return convertDateCallback(props.onClickMonth);
  }, [props.onClickMonth]);

  const onClickWeekNumber = React.useMemo(() => {
    return convertClickWeekNumberCallback(props.onClickWeekNumber);
  }, [props.onClickWeekNumber]);

  const onClickYear = React.useMemo(() => {
    return convertDateCallback(props.onClickYear);
  }, [props.onClickYear]);

  const onDrillDown = React.useMemo(() => {
    return convertViewCallback(props.onDrillDown);
  }, [props.onDrillDown]);

  const onDrillUp = React.useMemo(() => {
    return convertViewCallback(props.onDrillUp);
  }, [props.onDrillUp]);

  const tileClassName = React.useMemo(() => {
    return convertTileClassName(props.tileClassName);
  }, [props.tileClassName]);

  const tileContent = React.useMemo(() => {
    return convertTileContent(props.tileContent);
  }, [props.tileContent]);

  const tileDisabled = React.useMemo(() => {
    return convertTileDisabled(props.tileDisabled);
  }, [props.tileDisabled]);

  const value = React.useMemo(() => {
    return convertLocalDateToDate2(props.value);
  }, [props.value]);

  return (
    <ReactCalendar
      {...props}
      className={classNames("Calendar", props.className)}
      activeStartDate={activeStartDate}
      defaultActiveStartDate={defaultActiveStartDate}
      defaultValue={defaultValue}
      formatLongDate={formatLongDate}
      formatMonth={formatMonth}
      formatMonthYear={formatMonthYear}
      formatShortWeekday={formatShortWeekday}
      formatYear={formatYear}
      maxDate={maxDate}
      minDate={minDate}
      navigationLabel={navigationLabel}
      onActiveStartDateChange={onActiveStartDateChange}
      onChange={onChange}
      onViewChange={onViewChange}
      onClickDay={onClickDay}
      onClickDecade={onClickDecade}
      onClickMonth={onClickMonth}
      onClickWeekNumber={onClickWeekNumber}
      onClickYear={onClickYear}
      onDrillDown={onDrillDown}
      onDrillUp={onDrillUp}
      tileClassName={tileClassName}
      tileContent={tileContent}
      tileDisabled={tileDisabled}
      value={value}
    />
  );
};

export default React.memo(Calendar);
